import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, cfmt, pfmt } from '../lib/api';
import { TID } from '../constants/testIds';
import NewsletterForm from '../components/common/NewsletterForm';
import NeighbourhoodCard from '../components/common/NeighbourhoodCard';
import Sparkline from '../components/common/Sparkline';
import SEOHead from '../components/common/SEOHead';
import HeroVideoMontage from '../components/common/HeroVideoMontage';
import DecisionDashboard from '../components/common/DecisionDashboard';
import LifestyleGrid from '../components/common/LifestyleGrid';
import SouthernOntarioHub from '../components/common/SouthernOntarioHub';
import { ArrowUpRight } from 'lucide-react';

export default function Home() {
    const { data: featured } = useQuery({
        queryKey: ['featured-neighbourhoods'],
        queryFn: async () => (await api.get('/neighbourhoods', { params: { sort: 'forecast' } })).data,
    });
    const { data: invest } = useQuery({
        queryKey: ['invest-top-home'],
        queryFn: async () => (await api.get('/invest/top', { params: { limit: 5 } })).data,
    });
    const { data: articles } = useQuery({
        queryKey: ['home-articles'],
        queryFn: async () => (await api.get('/articles')).data,
    });

    const heroFeatured = featured?.items?.slice(0, 6) || [];
    const topInvest = invest?.top?.slice(0, 5) || [];
    const editorial = articles?.items?.slice(0, 3) || [];

    return (
        <div>
            <SEOHead
                title="Southern Ontario Neighbourhood Intelligence"
                description="Independent research on where to live across Durham, Hamilton, Halton, Brant and Niagara. Schools, safety, walkability, affordability and appreciation — one honest score."
                path="/"
            />
            {/* ==================== SECTION 1 · CINEMATIC HERO ==================== */}
            <section className="relative w-full overflow-hidden bg-black" style={{ minHeight: 'min(92vh, 900px)' }}>
                <HeroVideoMontage>
                    {/* Content — bottom-left editorial anchor */}
                    <div className="min-h-[inherit] h-full flex flex-col justify-end">
                        <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 pt-32 pb-16 lg:pb-24">
                            {/* small overline */}
                            <div
                                className="text-[11px] tracking-[0.24em] uppercase text-white/70 font-mono-data flex items-center gap-3 bn-fade-up"
                                data-testid="home-hero-eyebrow"
                            >
                                <span className="inline-block w-6 h-px bg-brand-gold/80" />
                                Southern Ontario Neighbourhood Intelligence
                            </div>

                            {/* Headline */}
                            <h1
                                className="font-editorial text-white mt-6 tracking-editorial bn-fade-up-delay-1"
                                style={{ fontSize: 'clamp(48px, 7.2vw, 104px)', lineHeight: 1.02, letterSpacing: '-0.03em' }}
                            >
                                Make Your Next Move
                                <br />
                                <span className="italic text-white/85">On Evidence, Not Instinct.</span>
                            </h1>

                            {/* Subhead */}
                            <p
                                className="mt-8 text-white/85 font-editorial max-w-3xl bn-fade-up-delay-2"
                                style={{ fontSize: 'clamp(17px, 1.35vw, 22px)', lineHeight: 1.55 }}
                            >
                                Find the right neighbourhood, understand the market, and make one of life&apos;s biggest financial decisions with clarity.
                            </p>

                            {/* CTAs */}
                            <div className="mt-10 flex flex-wrap items-center gap-3 bn-fade-up-delay-2">
                                <Link
                                    to="/match"
                                    data-testid="hero-cta-match"
                                    className="inline-flex items-center gap-2.5 bg-brand-gold text-brand-navy px-7 py-4 text-[14px] font-semibold rounded-lg border border-brand-gold hover:bg-white hover:border-white hover:-translate-y-0.5 transition-all duration-300 shadow-[0_10px_28px_rgba(11,31,54,0.25)] hover:shadow-[0_16px_40px_rgba(200,162,74,0.35)]"
                                    style={{ letterSpacing: '0.02em' }}
                                >
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-navy" />
                                    Take the Match Assessment
                                    <ArrowUpRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/neighbourhoods"
                                    data-testid="hero-cta-explore"
                                    className="inline-flex items-center gap-2 border border-white/50 text-white px-7 py-4 text-[14px] font-medium rounded-lg backdrop-blur-sm bg-white/[0.06] hover:bg-white hover:text-brand-navy hover:-translate-y-0.5 transition-all duration-300"
                                    style={{ letterSpacing: '0.02em' }}
                                >
                                    Explore Southern Ontario
                                </Link>
                            </div>

                            {/* Micro-caption */}
                            <p
                                className="mt-8 text-white/70 font-editorial italic"
                                style={{ fontSize: '15px', letterSpacing: '0.005em' }}
                                data-testid="hero-micro"
                            >
                                Independent research on 35 neighbourhoods across six regions. No listings, no commissions.
                            </p>

                            {/* Region rail */}
                            <div className="mt-14 pt-6 border-t border-white/15 flex flex-wrap items-center gap-x-8 gap-y-2 text-[11px] tracking-[0.22em] uppercase text-white/55 font-mono-data">
                                <span>Hamilton</span>
                                <span className="w-px h-3 bg-white/25" />
                                <span>Burlington</span>
                                <span className="w-px h-3 bg-white/25" />
                                <span>Oakville</span>
                                <span className="w-px h-3 bg-white/25" />
                                <span>Ancaster</span>
                                <span className="w-px h-3 bg-white/25" />
                                <span>Niagara</span>
                                <span className="w-px h-3 bg-white/25" />
                                <span>Durham</span>
                                <span className="w-px h-3 bg-white/25" />
                                <span>Brantford</span>
                                <span className="w-px h-3 bg-white/25" />
                                <span>Halton</span>
                            </div>
                        </div>
                    </div>
                </HeroVideoMontage>
            </section>

            {/* ==================== SECTION 2 · DECISION DASHBOARD ==================== */}
            <DecisionDashboard />

            {/* ==================== SECTION 3 · EXPLORE BY LIFESTYLE ==================== */}
            <LifestyleGrid />

            {/* ==================== SECTION 4 · SOUTHERN ONTARIO HUB ==================== */}
            <SouthernOntarioHub />

            {/* ==================== FEATURED NEIGHBOURHOODS ==================== */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <div className="overline">Featured</div>
                            <h2 className="font-editorial text-[36px] lg:text-[48px] tracking-editorial mt-3">Neighbourhoods worth watching</h2>
                        </div>
                        <Link to="/neighbourhoods" className="hidden md:inline-flex items-center gap-2 text-[13.5px] link-editorial">
                            All neighbourhoods <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div data-testid={TID.home.featuredList} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {heroFeatured.map((n) => <NeighbourhoodCard key={n.slug} n={n} />)}
                    </div>
                </div>
            </section>

            {/* ==================== INVESTMENT SNAPSHOT ==================== */}
            <section className="border-b border-white/5 bg-brand-navy text-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="overline text-brand-gold">The Terminal</div>
                        <h2 className="font-editorial text-[36px] lg:text-[44px] tracking-editorial mt-3 leading-tight">
                            Where the numbers are quietly moving.
                        </h2>
                        <p className="mt-5 text-[14px] text-white/60 max-w-md">
                            Five-year forecast, ranked. Updated with every new transaction.
                        </p>
                        <Link
                            to="/invest"
                            className="mt-8 inline-flex items-center gap-2 text-[13.5px] rounded-lg border border-white/30 px-5 py-2.5 hover:bg-brand-gold hover:text-brand-navy hover:border-brand-gold transition-all duration-300 hover:-translate-y-0.5"
                        >
                            Open investment view <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-8">
                        <div className="border border-white/10 rounded-lg overflow-hidden">
                            <div className="grid grid-cols-12 py-3 px-4 text-[11px] text-white/50 uppercase tracking-[0.18em] border-b border-white/10 bg-white/[0.03]">
                                <div className="col-span-4">Neighbourhood</div>
                                <div className="col-span-2 text-right">Median</div>
                                <div className="col-span-2 text-right">1Y</div>
                                <div className="col-span-2 text-right">5Y f&apos;cast</div>
                                <div className="col-span-2 text-right">Trend</div>
                            </div>
                            {topInvest.map((n) => (
                                <Link key={n.slug} to={`/neighbourhoods/${n.slug}`} className="grid grid-cols-12 py-4 px-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.04] items-center text-[14px] transition-colors">
                                    <div className="col-span-4">
                                        <div className="font-editorial text-[17px] tracking-editorial">{n.name}</div>
                                        <div className="text-[11px] text-white/40 uppercase tracking-wider mt-0.5">{n.city}</div>
                                    </div>
                                    <div className="col-span-2 text-right font-mono-data tabular">{cfmt(n.median_price)}</div>
                                    <div className={`col-span-2 text-right font-mono-data tabular ${n.price_1yr_change_pct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                                        {n.price_1yr_change_pct >= 0 ? '+' : ''}{pfmt(n.price_1yr_change_pct)}
                                    </div>
                                    <div className="col-span-2 text-right font-mono-data tabular text-brand-gold">+{pfmt(n.forecast_5yr_pct)}</div>
                                    <div className="col-span-2 flex justify-end">
                                        <Sparkline data={n.price_history} width={100} height={24} />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ==================== EDITORIAL ==================== */}
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <div className="overline">Insights</div>
                            <h2 className="font-editorial text-[36px] lg:text-[48px] tracking-editorial mt-3">Analysis before opinion.</h2>
                        </div>
                        <Link to="/insights" className="hidden md:inline-flex items-center gap-2 text-[13.5px] link-editorial">
                            All insights <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-neutral-200">
                        {editorial.map((a, i) => (
                            <Link key={a.slug} to={`/insights/${a.slug}`} className={`p-8 lg:p-10 border-b border-neutral-200 ${i < editorial.length - 1 ? 'md:border-r' : ''} hover:bg-[#F7F7F5]`}>
                                <div className="overline">{a.category} · {a.read_min} min read</div>
                                <div className="font-editorial text-[26px] mt-6 leading-tight tracking-editorial">{a.title}</div>
                                <div className="mt-4 text-[14px] text-neutral-600 leading-relaxed line-clamp-3">{a.dek}</div>
                                <div className="mt-6 text-[12px] text-neutral-500 font-mono-data">{a.published_at}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ==================== NEWSLETTER ==================== */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-6">
                        <div className="overline">Weekly briefing</div>
                        <h2 className="font-editorial text-[36px] lg:text-[52px] tracking-editorial mt-4 leading-[1.05]">
                            The neighbourhood letter.
                            <br />
                            <span className="italic text-neutral-400">Sunday. Short. Signal only.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:pl-16 flex flex-col justify-center">
                        <p className="text-[15px] text-neutral-600 max-w-md">
                            One neighbourhood, one number, one insight. Every Sunday morning. Free.
                        </p>
                        <div className="mt-6 max-w-md">
                            <NewsletterForm source="home" />
                        </div>
                        <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-neutral-400 font-mono-data">
                            No listings · No spam · Unsubscribe anytime
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
